import { CLIENT_STATE_KEYS } from '@thoughtspot/client-state-util';
import { copyTextToClipboard } from '@thoughtspot/radiant-react/widgets/_utils';
import { isFrontendTracingEnabled, isKPIChartEnhancementsEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import { ErrorTypes, getChartDataValidationError, } from './core/chart-data-validation';
import { canShowRegressionLine, getChartProperties, getChartType, hasPartialYAxisRange, isResponsiveLayoutEnabled, shouldShowDataLabels, } from './core/chart-model-utils';
import { chartProviders, getChartProvider, getLegendLabels, } from './core/chart-type-specification-service';
import { getGridLinesConfig } from './core/chart-ui-utils';
import { computeMetaDataInformation, processDataModel, } from './data/chart-data-processor-service';
import { generateQueryDefinitions, transformData } from './data/data-util';
import { otelTrace } from '@thoughtspot/observability';
import { getEnvOptions, initChartLibEnvOptions, isChartLibInitialized, } from './env';
import { initChartLibEnvOptionsHighcharts } from './env-highcharts';
import { BlinkHighchart } from './highcharts/blink-highchart';
import { updateDataLabels } from './highcharts/data-label.util';
import { generateHighchartConfig, setAxisRange, } from './highcharts/highchart-chart-util';
import { unbindAxisEvents } from './highcharts/highchart-axis-renderer';
import { getStyleClass, reflow, render, unmountChartReactRoots, updateClassNameForZoom, } from './renderer';
import { ChartType, } from './types';
import { createLogger } from './utils/logger';
import { OtelOp } from './utils/otel-operations';
import { measureBlockAsync } from './utils/perf-utils';
import { startSpan } from './utils/tracer';
export { getStyleClass };
export var ChartEventTypes;
(function (ChartEventTypes) {
    ChartEventTypes["PointClick"] = "PointClick";
    ChartEventTypes["PointDoubleClick"] = "PointDoubleClick";
    ChartEventTypes["ContextClick"] = "ContextClick";
    ChartEventTypes["MouseOver"] = "MouseOver";
    ChartEventTypes["MouseOut"] = "MouseOut";
    ChartEventTypes["LegendClick"] = "LegendClick";
    ChartEventTypes["LegendColorChange"] = "LegendColorChange";
    ChartEventTypes["AxisClick"] = "AxisClick";
    ChartEventTypes["PivotColumnClick"] = "PivotColumnClick";
    ChartEventTypes["ColumnSort"] = "ColumnSort";
    ChartEventTypes["RenderComplete"] = "RenderComplete";
    ChartEventTypes["ToggleGridline"] = "ToggleGridline";
    ChartEventTypes["UpdateClientState"] = "UpdateClientState";
    ChartEventTypes["UpdateAxisConfigAndClientState"] = "UpdateAxisConfigAndClientState";
    ChartEventTypes["UpdateZoomEvent"] = "UpdateZoomEvent";
    ChartEventTypes["ChangeAnalysisClick"] = "ChangeAnalysisClick";
    ChartEventTypes["ExceptionRaised"] = "ExceptionRaised";
    ChartEventTypes["ChartAreaClick"] = "ChartAreaClick";
    ChartEventTypes["VizRenderUpdate"] = "VizRenderUpdate";
})(ChartEventTypes || (ChartEventTypes = {}));
const logger = createLogger('chart-lib-index');
export class Chart {
    constructor(el, chartModel, options, chartOptions = {}) {
        var _a, _b;
        this.destroyed = false;
        this.lastReinitSpanFingerprint = null;
        this.toggleDataLabel = (enabled, columnId) => {
            if (this.chart instanceof BlinkHighchart) {
                this.chart.setDatalabelVisibilityInUserOptions(enabled, columnId);
                updateDataLabels(this.chart, this.chartModel, this.dataModel, columnId);
            }
            else if (this.chart.isGeoMap) {
                this.chart.setDataLabelVisibility(enabled);
            }
        };
        this.setStackLabelVisibility = (enabled) => {
            if (_.isFunction(this.chart.setStackLabelVisibility)) {
                this.chart.setStackLabelVisibility(enabled);
            }
        };
        this.toggleAllDataLabels = (enabled) => {
            if (this.chart) {
                _.forEach(this.chart.userOptions.series, serie => {
                    serie.dataLabels = enabled;
                });
            }
        };
        this.el = el;
        this.options = getEnvOptions(options);
        this.otelEnabled = (_a = options.otelEnabled) !== null && _a !== void 0 ? _a : false;
        this.otelContext = (_b = options.otelContext) !== null && _b !== void 0 ? _b : null;
        this.eventsMap = new Map();
        this.chartOptions = _.assign({}, chartOptions);
        if (!isChartLibInitialized()) {
            initChartLibEnvOptions(this.options);
            initChartLibEnvOptionsHighcharts();
        }
        this.prevRenderState = _.assign({});
        try {
            this.initChartViz(chartModel);
        }
        catch (e) {
            this.processGenericError(e);
        }
    }
    getDataError() {
        return this._error;
    }
    getEnvOptions() {
        return this.options;
    }
    reinitializeChartViz(chartModel) {
        var _a, _b;
        if (((_a = this.chartModel.vizProp.systemSeriesColors) === null || _a === void 0 ? void 0 : _a.length) !== 0 &&
            ((_b = chartModel.vizProp.systemSeriesColors) === null || _b === void 0 ? void 0 : _b.length) === 0) {
            return;
        }
        try {
            this.initChartViz(chartModel, 'reinitialize');
        }
        catch (e) {
            logger.error(e);
            this._error = { type: ErrorTypes.FAILED_TO_RENDER_VIZ };
        }
        if (!!this.chart && _.isFunction(this.chart.updateChartModel)) {
            this.chart.updateChartModel(this.chartModel);
        }
    }
    onDestroy() {
        var _a, _b;
        this.destroyed = true;
        if (!!this.chart && _.isFunction(this.chart.onDestroy)) {
            this.chart.onDestroy();
        }
        if (this.el) {
            unmountChartReactRoots(this.el);
        }
        if ((_a = this.chartModel) === null || _a === void 0 ? void 0 : _a.id) {
            unbindAxisEvents(this.chartModel.id);
        }
        this.el = undefined;
        this.data = undefined;
        this.dataModel = undefined;
        this.chartModel = undefined;
        this.chart = undefined;
        (_b = this.eventsMap) === null || _b === void 0 ? void 0 : _b.clear();
        this.metaInfo = undefined;
        this.queryDefinitions = undefined;
        this.allSeriesIds = undefined;
    }
    initChartViz(chartModel, initType = 'initial') {
        this.chartModel = _.assign({}, chartModel);
        this.queryDefinitions = generateQueryDefinitions(this.chartModel);
        this.setData(chartModel.data, initType);
        if (this._error) {
            return;
        }
        this.allSeriesIds = getLegendLabels(this.chartModel, this.dataModel).map(legendLabel => legendLabel.id);
    }
    processGenericError(e) {
        logger.error(e);
        const message = e === null || e === void 0 ? void 0 : e.message;
        if (Object.values(ErrorTypes).includes(message)) {
            this._error = { type: message };
        }
        else {
            this._error = { type: ErrorTypes.FAILED_TO_RENDER_VIZ };
        }
    }
    setData(data, initType = 'initial') {
        if (data) {
            this.data = transformData(data);
            this.processData(initType);
            this._error = this._error || this.processDataError();
        }
    }
    processDataError() {
        const dataValidationError = getChartDataValidationError(this.dataModel, this.chartModel);
        if (dataValidationError) {
            return dataValidationError;
        }
        return undefined;
    }
    hasNoData() {
        if (!this.data) {
            return true;
        }
        const emptyDataArray = this.data.filter(res => {
            return res.data.length === 0;
        });
        if (emptyDataArray.length > 0) {
            return true;
        }
        return false;
    }
    computeReinitFingerprint() {
        var _a, _b;
        try {
            return JSON.stringify((_b = (_a = this.chartModel) === null || _a === void 0 ? void 0 : _a.vizProp) !== null && _b !== void 0 ? _b : {});
        }
        catch (_c) {
            return '';
        }
    }
    processData(initType = 'initial') {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let span;
        if (initType === 'reinitialize' && isFrontendTracingEnabled()) {
            const fingerprint = this.computeReinitFingerprint();
            const isNoOpReinit = this.lastReinitSpanFingerprint !== null &&
                fingerprint === this.lastReinitSpanFingerprint;
            this.lastReinitSpanFingerprint = fingerprint;
            span = isNoOpReinit
                ? null
                : startSpan(OtelOp.DATA_REINITIALIZE, this.otelContext, this.otelEnabled);
        }
        else {
            span = startSpan(initType === 'reinitialize'
                ? OtelOp.DATA_REINITIALIZE
                : OtelOp.DATA_PROCESS, this.otelContext, this.otelEnabled);
        }
        const t0 = Date.now();
        let hadError = false;
        try {
            const isKpiChart = this.chartType === ChartType.Kpi;
            const shouldBypassNoDataForKpi = isKpiChart && isKPIChartEnhancementsEnabled();
            if (this.hasNoData() && !shouldBypassNoDataForKpi) {
                this._error = { type: ErrorTypes.NO_DATA };
                return;
            }
            if (this.hasNoData() && shouldBypassNoDataForKpi) {
                return;
            }
            this.dataModel = processDataModel(this.chartModel, this.data, this.queryDefinitions, this.getEnvOptions(), this.chartOptions, () => {
                return this.eventsMap;
            });
            if (this.hasNoData()) {
                this._error = { type: ErrorTypes.NO_DATA };
                return;
            }
            this.metaInfo = computeMetaDataInformation(this.chartModel, this.dataModel);
        }
        catch (e) {
            hadError = true;
            span === null || span === void 0 ? void 0 : span.addEvent('data.process.error', {
                'error.message': String((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : '').slice(0, 500),
            });
            span === null || span === void 0 ? void 0 : span.setStatus({ code: 2 });
            throw e;
        }
        finally {
            const rowCount = ((_b = this.data) !== null && _b !== void 0 ? _b : []).reduce((sum, q) => { var _a, _b; return sum + ((_b = (_a = q === null || q === void 0 ? void 0 : q.data) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
            span === null || span === void 0 ? void 0 : span.setAttributes({
                'data.chart_type': String((_c = this.chartType) !== null && _c !== void 0 ? _c : ''),
                'data.init_type': initType,
                'data.series_count': (_e = (_d = this.data) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0,
                'data.row_count': rowCount,
                'data.col_count': (_h = (_g = (_f = this.chartModel) === null || _f === void 0 ? void 0 : _f.columns) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0,
                'data.had_error': hadError,
                'data.duration_ms': Date.now() - t0,
            });
            if (!hadError) {
                span === null || span === void 0 ? void 0 : span.setStatus({ code: 1 });
            }
            span === null || span === void 0 ? void 0 : span.end();
        }
    }
    getRenderOptions() {
        const { isDataSourceDefault, disableColorRotation, shouldShowHighchartTitle, isPrintMode, hideAxisIcon, } = this.chartOptions;
        const enableGridline = this.options.flags.isGridlinesEnabledByDefault;
        const { xGridlineEnabled, yGridlineEnabled } = getGridLinesConfig(this.chartModel, enableGridline);
        return {
            xGridlineEnabled,
            yGridlineEnabled,
            disableColorRotation,
            isDataSourceDefault,
            shouldShowHighchartTitle,
            enablePiechartAnimation: !isPrintMode,
            hideAxisIcon,
        };
    }
    enableXAxisGridLines(enabled) {
        this.chart.enableXAxisGridLines(enabled);
    }
    enableYAxisGridLines(enabled) {
        this.chart.enableYAxisGridLines(enabled);
    }
    getDataModel() {
        return this.dataModel;
    }
    get chartType() {
        return getChartType(this.chartModel);
    }
    on(eventType, handler) {
        if (this.eventsMap.has(eventType)) {
            logger.warn(`An event handler for ${eventType} exists and will be overridden`);
        }
        this.eventsMap.set(eventType, handler);
        return this;
    }
    updateSeriesVisibility(visibleSeriesNames) {
        var _a;
        _.set(this.chartModel, [
            'vizProp',
            CLIENT_STATE_KEYS.chartProperties,
            CLIENT_STATE_KEYS.visibleSeriesNames,
        ], [...visibleSeriesNames]);
        if (this.chart instanceof BlinkHighchart) {
            updateDataLabels(this.chart, this.chartModel, this.dataModel);
        }
        try {
            this.chart.setVisibleSeriesVisibility(new Set([...visibleSeriesNames]));
            this.redraw();
            if (this.chart instanceof BlinkHighchart &&
                hasPartialYAxisRange(this.chartModel)) {
                setAxisRange(this.chart, this.chartModel);
                this.redraw();
            }
            const xAxis = (_a = this.chart.axes) === null || _a === void 0 ? void 0 : _a[0];
            if (xAxis && xAxis.dataMin > xAxis.tickPositions[0]) {
                this.reflow();
            }
        }
        catch (e) {
            logger.error('unable to update Series Visibility', e);
            this.processGenericError(e);
        }
    }
    updateChartColor(props, val, renderOnUpdate = true) {
        var _a;
        _.set(this.chartModel, ['vizProp', ...props], val);
        if ((_a = this.chartModel.visualOverrides) === null || _a === void 0 ? void 0 : _a.overrides) {
            this.chartModel.visualOverrides = {
                ...this.chartModel.visualOverrides,
                overrides: null,
            };
        }
        if (renderOnUpdate) {
            this.render();
        }
    }
    getVizModel() {
        return this.chartModel;
    }
    getSelectedPoints() {
        return this.chart.getSelectedPoints();
    }
    getChartContainer() {
        var _a, _b;
        return (_b = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.container) !== null && _b !== void 0 ? _b : null;
    }
    isInPanMode() {
        return (this.chart &&
            _.isFunction(this.chart.isInPanMode) &&
            this.chart.isInPanMode());
    }
    switchChartZoomMode() {
        if (this.el) {
            updateClassNameForZoom(this.el);
        }
        if (this.isInPanMode()) {
            this.chart.switchToZoomMode();
        }
        else {
            this.chart.switchToPanMode();
        }
        this.chart.resetPointer();
    }
    chartZoomOut() {
        this.chart.zoomOut();
    }
    togglePivotSummaries(updatedChartModel) {
        if (this.chart) {
            if (this.chart.togglePivotSummaries) {
                this.chart.togglePivotSummaries(updatedChartModel);
            }
            else {
                logger.warn('chart object is missing the togglePivotSummaries method');
            }
        }
    }
    setMarkerVisibility(markerVisible) {
        this.chart.setMarkerVisibility(markerVisible);
    }
    setAxisRangeForAxes(id, range) {
        const axisIdx = _.findIndex(this.chart.axes, axis => _.get(axis, 'userOptions.id') === id);
        this.chart.setAxisRange(axisIdx, range, false);
    }
    getConfig() {
        const chartProvider = getChartProvider(this.chartType, !!this.chartModel.cortexData);
        const generateConfig = getConfigGeneratorForProvider(chartProvider);
        const config = generateConfig(this.chartModel, this.dataModel, this.el.getBoundingClientRect(), this.eventsMap, this.getRenderOptions(), this.options);
        return config._highchartConfig ? config._highchartConfig : config;
    }
    redraw() {
        this.chart.redraw();
    }
    hasCustomDownload() {
        var _a;
        return !!((_a = this.chart) === null || _a === void 0 ? void 0 : _a.hasCustomDownload);
    }
    hasInternalChartError() {
        var _a, _b;
        return ((_a = this.chart) === null || _a === void 0 ? void 0 : _a.hasInternalChartError)
            ? (_b = this.chart) === null || _b === void 0 ? void 0 : _b.hasInternalChartError()
            : false;
    }
    downloadChart(name) {
        if (_.isFunction(this.chart.downloadChart)) {
            this.chart.downloadChart(name);
        }
    }
    setThemeMode(mode) {
        if (_.isFunction(this.chart.setThemeMode)) {
            this.chart.setThemeMode(mode);
        }
    }
    async render() {
        if (this.destroyed) {
            return this;
        }
        await measureBlockAsync('ChartPrimaryAndSecondaryRender', async () => {
            await this.primaryRender();
            await this.secondaryRender();
        });
        return this;
    }
    async secondaryRender(parentCtx) {
        var _a, _b, _c;
        if (this.destroyed) {
            return;
        }
        const span = startSpan(OtelOp.RENDER_SECONDARY, parentCtx !== null && parentCtx !== void 0 ? parentCtx : this.otelContext, this.otelEnabled);
        const t0 = Date.now();
        let redrawCalled = false;
        let hasStackLabels = false;
        let hadError = false;
        await measureBlockAsync('secondaryRender', async () => {
            var _a, _b, _c, _d;
            if (!this.chart) {
                logger.warn('chart is not rendered, chart object is missing');
                return;
            }
            try {
                hasStackLabels = (_c = (_b = (_a = this.chart).hasStackLabels) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : false;
                await this.chart.secondaryRender(this.chartModel, this.dataModel);
                redrawCalled = true;
            }
            catch (e) {
                hadError = true;
                logger.warn('secondary render failed', e);
                span.addEvent('render.error', {
                    'error.message': String((_d = e === null || e === void 0 ? void 0 : e.message) !== null && _d !== void 0 ? _d : '').slice(0, 500),
                });
                this.processGenericError(e);
            }
        });
        span.setAttributes({
            'render.has_data_labels': hasStackLabels,
            'render.has_data_labels_update': (_c = (_b = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.didUpdateDataLabels) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : false,
            'render.redraw_called': redrawCalled,
            'render.axis_events_bound': !hadError,
            'render.duration_ms': Date.now() - t0,
        });
        span.setStatus({ code: hadError ? 2 : 1 });
        span.end();
    }
    async primaryRender(parentCtx, renderIndex) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        if (this.destroyed) {
            return;
        }
        const container = this.el;
        const resolvedCtx = parentCtx !== null && parentCtx !== void 0 ? parentCtx : this.otelContext;
        const op = (renderIndex !== null && renderIndex !== void 0 ? renderIndex : 1) === 1
            ? OtelOp.RENDER_PRIMARY
            : OtelOp.RENDER_UPDATE;
        const span = startSpan(op, resolvedCtx, this.otelEnabled);
        const primaryCtx = (this.otelEnabled && resolvedCtx)
            ? otelTrace.setSpan(resolvedCtx, span)
            : resolvedCtx;
        const t0 = Date.now();
        try {
            if (!!this.chart &&
                !this.chart.isReactManaged &&
                _.isFunction(this.chart.onDestroy)) {
                try {
                    this.chart.onDestroy();
                }
                catch (e) {
                    logger.warn('destroying previous chart failed', e);
                }
            }
            await measureBlockAsync('primaryRenderingWithougtReflow', async () => {
                const { chart, prevRenderState } = await render(this, container, this.chartType, this.chartModel, this.dataModel, this.eventsMap, this.getRenderOptions(), this.options, this.prevRenderState, this.chartOptions, this.getDataError());
                this.chart = chart;
                this.prevRenderState = prevRenderState;
            });
            if (this.destroyed) {
                if (!!this.chart && _.isFunction(this.chart.onDestroy)) {
                    this.chart.onDestroy();
                }
                this.chart = null;
                if (container && !container.isConnected) {
                    unmountChartReactRoots(container);
                }
                return this;
            }
            const legendLabels = (_a = getLegendLabels(this.chartModel, this.dataModel)) !== null && _a !== void 0 ? _a : [];
            const legendPosition = (_c = (_b = getChartProperties(this.chartModel)) === null || _b === void 0 ? void 0 : _b.legendPosition) !== null && _c !== void 0 ? _c : '';
            const renderOpts = this.getRenderOptions();
            span.setAttributes({
                'render.provider': (_d = getChartProvider(this.chartType)) !== null && _d !== void 0 ? _d : '',
                'render.chart_type': String((_e = this.chartType) !== null && _e !== void 0 ? _e : ''),
                'render.series_count': (_h = (_g = (_f = this.dataModel) === null || _f === void 0 ? void 0 : _f.series) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0,
                'render.data_point_count': ((_k = (_j = this.dataModel) === null || _j === void 0 ? void 0 : _j.series) !== null && _k !== void 0 ? _k : []).reduce((sum, s) => { var _a, _b; return sum + ((_b = (_a = s === null || s === void 0 ? void 0 : s.data) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0),
                'render.has_conditional_formatting': !!((_o = (_m = (_l = this.chartModel) === null || _l === void 0 ? void 0 : _l.vizProps) === null || _m === void 0 ? void 0 : _m.conditionalFormatting) === null || _o === void 0 ? void 0 : _o.length),
                'render.is_print_mode': !!((_p = this.chartOptions) === null || _p === void 0 ? void 0 : _p.isPrintMode),
                'render.is_scaled': !!((_q = this.prevRenderState) === null || _q === void 0 ? void 0 : _q.lastRenderScaled),
                'render.legend_count': legendLabels.length,
                'render.legend_orientation': legendPosition
                    ? String(legendPosition)
                    : 'default',
                'render.has_data_labels': shouldShowDataLabels(this.chartModel, this.dataModel, (_t = (_s = (_r = this.options) === null || _r === void 0 ? void 0 : _r.flags) === null || _s === void 0 ? void 0 : _s.maxLabelCount) !== null && _t !== void 0 ? _t : 1000, (_w = (_v = (_u = this.options) === null || _u === void 0 ? void 0 : _u.flags) === null || _v === void 0 ? void 0 : _v.dataBatchSize) !== null && _w !== void 0 ? _w : 5000),
                'render.has_regression_line': canShowRegressionLine(this.chartModel),
                'render.has_gridlines': renderOpts.xGridlineEnabled ||
                    renderOpts.yGridlineEnabled,
                'render.is_responsive_layout': isResponsiveLayoutEnabled(this.chartModel),
                'render.kpi_optimization_used': this.chartType === ChartType.Kpi &&
                    isKPIChartEnhancementsEnabled(),
                'render.render_queue_used': true,
                'render.index': renderIndex !== null && renderIndex !== void 0 ? renderIndex : 1,
                'render.duration_ms': Date.now() - t0,
            });
            span.setStatus({ code: 1 });
            await this.reflow(undefined, primaryCtx);
        }
        catch (err) {
            span.addEvent('render.failed_to_render', {
                'error.type': (_x = err === null || err === void 0 ? void 0 : err.errorType) !== null && _x !== void 0 ? _x : 'UNKNOWN',
                'error.message': String((_y = err === null || err === void 0 ? void 0 : err.message) !== null && _y !== void 0 ? _y : '').slice(0, 500),
            });
            span.setStatus({ code: 2, message: String((_z = err === null || err === void 0 ? void 0 : err.message) !== null && _z !== void 0 ? _z : '') });
            logger.error('unable to render the chart', err);
            this.processGenericError(err);
            throw new Error(err);
        }
        finally {
            span.end();
        }
        return this;
    }
    async reflow(chartOptions, parentCtx) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        if (this.destroyed) {
            return this;
        }
        const span = startSpan(OtelOp.RENDER_REFLOW, parentCtx !== null && parentCtx !== void 0 ? parentCtx : this.otelContext, this.otelEnabled);
        const t0 = Date.now();
        const rect = (_b = (_a = this.el) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect) === null || _b === void 0 ? void 0 : _b.call(_a);
        const prevWidth = (_f = (_c = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.prevWidth) !== null && _c !== void 0 ? _c : (_e = (_d = this.prevRenderState) === null || _d === void 0 ? void 0 : _d.vizContainer) === null || _e === void 0 ? void 0 : _e.prevVizContainerWidth) !== null && _f !== void 0 ? _f : 0;
        const prevHeight = (_k = (_g = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.prevHeight) !== null && _g !== void 0 ? _g : (_j = (_h = this.prevRenderState) === null || _h === void 0 ? void 0 : _h.vizContainer) === null || _j === void 0 ? void 0 : _j.prevVizContainerHeight) !== null && _k !== void 0 ? _k : 0;
        let reflowResult = {};
        let hadError = false;
        await measureBlockAsync('chartReflow', async () => {
            var _a;
            if (this.chart) {
                try {
                    const result = await reflow(this, this.chart, this.chartModel, this.dataModel, this.el, (this.chartOptions = _.assignWith(this.chartOptions, chartOptions)), this.prevRenderState, this.eventsMap, this.options, this.getRenderOptions());
                    this.prevRenderState = result.prevRenderState;
                    reflowResult = result;
                }
                catch (e) {
                    hadError = true;
                    span.addEvent('render.error', {
                        'error.message': String((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : '').slice(0, 500),
                    });
                    span.setStatus({ code: 2 });
                    logger.warn('chart reflow was erroneous', e);
                    this.processGenericError(e);
                }
            }
        });
        span.setAttributes({
            'reflow.trigger': (_l = chartOptions === null || chartOptions === void 0 ? void 0 : chartOptions.reflowTrigger) !== null && _l !== void 0 ? _l : 'explicit_reflow',
            'reflow.prev_width': prevWidth,
            'reflow.prev_height': prevHeight,
            'reflow.new_width': (_m = rect === null || rect === void 0 ? void 0 : rect.width) !== null && _m !== void 0 ? _m : 0,
            'reflow.new_height': (_o = rect === null || rect === void 0 ? void 0 : rect.height) !== null && _o !== void 0 ? _o : 0,
            'reflow.breakpoint_changed': (_p = reflowResult.breakpointChanged) !== null && _p !== void 0 ? _p : false,
            'reflow.legend_unmounted': (_q = reflowResult.legendUnmounted) !== null && _q !== void 0 ? _q : false,
            'reflow.legend_remounted': (_r = reflowResult.legendRemounted) !== null && _r !== void 0 ? _r : false,
            'reflow.skip_reason': (_s = reflowResult.skipReason) !== null && _s !== void 0 ? _s : '',
            'reflow.setsize_called': (_t = reflowResult.setsizeCalled) !== null && _t !== void 0 ? _t : false,
            'reflow.raf_used': (_u = reflowResult.rafUsed) !== null && _u !== void 0 ? _u : false,
            'reflow.duration_ms': Date.now() - t0,
        });
        if (!hadError) {
            span.setStatus({ code: 1 });
        }
        span.end();
        return this;
    }
    copyToClipboard() {
        var _a, _b;
        let textToBeCopied;
        switch (this.chartType) {
            case ChartType.PivotTable:
                textToBeCopied = (_b = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.clickedCell) === null || _b === void 0 ? void 0 : _b.text;
                break;
            default:
        }
        if (textToBeCopied !== undefined)
            copyTextToClipboard(textToBeCopied);
    }
}
function noOpConfigGenerator() {
    return {};
}
function getConfigGeneratorForProvider(chartProvider) {
    switch (chartProvider) {
        case chartProviders.HIGHCHART:
            return generateHighchartConfig;
        default:
            return noOpConfigGenerator;
    }
}
//# sourceMappingURL=index.js.map